import { useState } from 'react'
import type { Trip } from '../../api/types'
import { useZonenverlauf } from '../../hooks/queries'
import { rueckfrage } from '../../components/Rueckfrage'
import { fehlertext } from '../../api/client'
import { zeitText, zuFeld, versatzText } from '../../lib/ortszeit'
import {
  alleZonen,
  gleicheZone,
  istSommerzeit,
  stadtVon,
  umstellungenIn,
  versatzIn,
  wanduhrZuUtc,
  zoneDesGeraets,
  type Zoneneintrag,
} from '../../lib/zonen'
import { useTexte } from '../../texte'

/**
 * The trip's zone course on its page (1.87.0) -- which zone applied from
 * when. Only for the author; readers get the course with the trip and see
 * its effect, not the list.
 *
 * **A start is a wall clock in the zone being chosen**: "New York from
 * 2nd October, 08:00" means eight in New York, and that is what the author
 * read off a clock there. The server gets the moment (UTC), and the list
 * shows each start in its own zone again.
 *
 * The first entry has no start and covers everything before the next --
 * photos from before the trip was created too. Its name can change, its
 * start cannot, and it cannot be removed; the server holds the same.
 *
 * **Under each entry its changes of offset** (1.89.0), grey and without a
 * button: derived from the name for the span the entry covers, never
 * stored (`umstellungenIn`). And at the entry the offset says whether it is
 * summer time -- "Berlin UTC+2" in November read as if it were computed
 * with +2.
 */
export function Zonenverlauf({ trip }: { trip: Trip }) {
  const t = useTexte().reise.info.zonen
  const verlauf = useZonenverlauf(trip.id)
  const [bearbeitet, setBearbeitet] = useState<Zoneneintrag | 'neu' | null>(null)
  const [neuGerechnet, setNeuGerechnet] = useState<number | null>(null)
  const fehler = verlauf.anlegen.error ?? verlauf.aendern.error ?? verlauf.entfernen.error
  const art = (name: string, wann: number) => {
    const sommer = istSommerzeit(name, wann)
    return sommer === null ? null : sommer ? t.sommerzeit : t.winterzeit
  }
  const spannen = zeitraeume(trip)

  return (
    <div className="card card--seite" data-schluessel="zonenverlauf">
      <h2 className="card__titel">{t.titel}</h2>
      <p className="card__lead card__lead--knapp">{t.hinweis}</p>
      <p className="card__lead card__lead--knapp" data-schluessel="local-time-hint">{t.localTimeHint}</p>

      <ul className="zonenverlauf__liste">
        {trip.zones.map((eintrag, i) => {
          const [von, bis] = spannen[i] ?? [0, 0]
          const zuBeginn = eintrag.valid_from === null ? von : new Date(eintrag.valid_from).getTime()
          const wechsel = umstellungenIn(eintrag.tz_name, zuBeginn, bis)
          return (
            <li key={eintrag.id} data-zone={eintrag.tz_name}>
              <div className="row row--wrap">
                <span className="bar__grow">
                  <span className="fett">{stadtVon(eintrag.tz_name)}</span>{' '}
                  <span className="leise">
                    {t.versatzMitArt(versatzText(versatzIn(eintrag.tz_name, zuBeginn)), art(eintrag.tz_name, zuBeginn))}
                  </span>
                  <br />
                  <span className="leise">
                    {eintrag.valid_from === null
                      ? t.vonAnfang
                      : t.ab(zeitText(eintrag.valid_from, versatzIn(eintrag.tz_name, eintrag.valid_from)))}
                  </span>
                </span>
                <button type="button" className="button button--outline button--schmal" onClick={() => setBearbeitet(eintrag)}>
                  {t.aendern}
                </button>
                {eintrag.valid_from !== null && (
                  <button
                    type="button"
                    className="button button--outline button--gefahr button--schmal"
                    disabled={verlauf.entfernen.isPending}
                    onClick={async () => {
                      if (!(await rueckfrage(t.entfernenFrage(stadtVon(eintrag.tz_name)), t.entfernen))) return
                      verlauf.entfernen.mutate(eintrag.id, {
                        onSuccess: (antwort) => setNeuGerechnet(antwort.recomputed),
                      })
                    }}
                  >
                    {t.entfernen}
                  </button>
                )}
              </div>
              {wechsel.length > 0 && (
                <ul className="zonenverlauf__umstellungen" data-schluessel="umstellungen">
                  {wechsel.map((u) => (
                    <li key={u.ab} className="leise klein" data-umstellung={new Date(u.ab).toISOString()}>
                      {t.umstellung(
                        zeitText(new Date(u.ab).toISOString(), u.versatz),
                        versatzText(u.versatz),
                        art(eintrag.tz_name, u.ab),
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      {fehler && <p className="notice" role="alert">{fehlertext(fehler)}</p>}
      {neuGerechnet !== null && neuGerechnet > 0 && (
        <p className="feld__hinweis feld__hinweis--block" role="status">{t.neuGerechnet(neuGerechnet)}</p>
      )}

      {bearbeitet === null ? (
        <button type="button" className="button button--outline" onClick={() => setBearbeitet('neu')}>
          {t.hinzufuegen}
        </button>
      ) : (
        <Zonenformular
          key={bearbeitet === 'neu' ? 'neu' : bearbeitet.id}
          eintrag={bearbeitet === 'neu' ? null : bearbeitet}
          vorhanden={trip.zones.map((z) => z.tz_name)}
          wirdGespeichert={verlauf.anlegen.isPending || verlauf.aendern.isPending}
          onAbbrechen={() => setBearbeitet(null)}
          onSpeichern={(angaben) => {
            const fertig = {
              onSuccess: (antwort: { recomputed: number }) => {
                setNeuGerechnet(antwort.recomputed)
                setBearbeitet(null)
              },
            }
            if (bearbeitet === 'neu') verlauf.anlegen.mutate(angaben, fertig)
            else verlauf.aendern.mutate({ id: bearbeitet.id, ...angaben }, fertig)
          }}
        />
      )}
    </div>
  )
}

const TAG_MS = 24 * 60 * 60_000

/**
 * The span each entry covers, as [from, to) in ms: from its start -- the
 * first from the trip's first day, or from its creation -- to the next
 * entry, the last to the trip's end. A trip without an end looks half a
 * year ahead, so that the next change already stands there.
 *
 * The trip's days are calendar days without a zone; a day before and after
 * is added, so that the first and the last local day are inside the span
 * wherever the zone lies (UTC−12 to UTC+14).
 */
function zeitraeume(trip: Trip): Array<[number, number]> {
  const anfang = trip.starts_on !== null
    ? Date.parse(`${trip.starts_on}T00:00:00Z`) - TAG_MS
    : new Date(trip.created_at).getTime()
  const ende = trip.ends_on !== null
    ? Date.parse(`${trip.ends_on}T00:00:00Z`) + 2 * TAG_MS
    : Date.now() + 183 * TAG_MS
  return trip.zones.map((eintrag, i) => {
    const von = eintrag.valid_from === null ? anfang : new Date(eintrag.valid_from).getTime()
    const naechster = trip.zones[i + 1]?.valid_from
    const bis = naechster != null ? new Date(naechster).getTime() : ende
    return [von, bis]
  })
}

/** The fields for one entry: a zone, and -- except for the first -- from when. */
function Zonenformular({
  eintrag,
  vorhanden,
  wirdGespeichert,
  onAbbrechen,
  onSpeichern,
}: {
  eintrag: Zoneneintrag | null
  vorhanden: string[]
  wirdGespeichert: boolean
  onAbbrechen: () => void
  onSpeichern: (angaben: { tz_name: string; valid_from?: string }) => void
}) {
  const texte = useTexte()
  const t = texte.reise.info.zonen
  const [name, setName] = useState(eintrag?.tz_name ?? zoneDesGeraets())
  const ohneAnfang = eintrag !== null && eintrag.valid_from === null
  // The start as the wall clock of the chosen zone -- now, for a new entry.
  const [ab, setAb] = useState(() => {
    const wann = eintrag?.valid_from ?? new Date().toISOString()
    return zuFeld(wann, versatzIn(eintrag?.tz_name ?? zoneDesGeraets(), wann))
  })
  const geraet = zoneDesGeraets()

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        if (ohneAnfang) {
          onSpeichern({ tz_name: name })
          return
        }
        const moment = wanduhrZuUtc([{ id: 0, valid_from: null, tz_name: name }], ab)
        if (moment === undefined) return
        onSpeichern({ tz_name: name, valid_from: moment })
      }}
    >
      <label className="field field--dicht">
        <span className="field__label">{t.feldZone}</span>
        <select className="field__input" value={name} onChange={(event) => setName(event.target.value)}>
          {alleZonen([...vorhanden, name]).map((zone) => (
            <option key={zone} value={zone}>
              {zone.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>
      {!gleicheZone(name, geraet) && (
        <button type="button" className="button button--outline" onClick={() => setName(geraet)}>
          {t.geraet(stadtVon(geraet))}
        </button>
      )}

      {ohneAnfang ? (
        <span className="feld__hinweis feld__hinweis--block feld__hinweis--nah">{t.ersterHinweis}</span>
      ) : (
        <label className="field field--dicht">
          <span className="field__label">{t.feldAb(stadtVon(name))}</span>
          <input
            className="field__input"
            type="datetime-local"
            required
            value={ab}
            onChange={(event) => setAb(event.target.value)}
          />
        </label>
      )}

      <div className="row">
        <button className="button" type="submit" disabled={wirdGespeichert}>
          {wirdGespeichert ? texte.reise.info.wirdGespeichert : texte.allgemein.speichern}
        </button>
        <button className="button button--outline" type="button" onClick={onAbbrechen}>
          {texte.allgemein.abbrechen}
        </button>
      </div>
    </form>
  )
}
